import React, { useEffect, useMemo, useState } from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonRow,
    IonSearchbar,
    IonText,
    IonToggle,
} from "@ionic/react";
import type { ToggleCustomEvent } from "@ionic/core/components";
import { addOutline, addSharp } from "ionicons/icons";
import type { Code } from "../AcaSettingsTypes";

interface AcaSettingsFailedReasonSectionProps {
    data: {
        codes: Code[];
    };
    actions: {
        openAdd: () => void;
        toggleCode: (code: Code, ev?: ToggleCustomEvent) => void;
    };
}

const AcaSettingsFailedReasonSection: React.FC<AcaSettingsFailedReasonSectionProps> = ({data, actions,}) => {
    const { codes } = data;
    const { openAdd, toggleCode } = actions;

    const [codeQuery, setCodeQuery] = useState("");
    const [codesExpanded, setCodesExpanded] = useState<string[]>([]);

    const activeCodes = useMemo(
        () =>
            (codes || [])
                .filter((code) => code.status === "ACTIVE")
                .filter((code) => (code.description || "").toLowerCase().includes(codeQuery.toLowerCase()))
                .sort((a, b) => a.description.localeCompare(b.description)),
        [codes, codeQuery]
    );

    const inactiveCodes = useMemo(
        () =>
            (codes || [])
                .filter((code) => code.status === "INACTIVE")
                .filter((code) => (code.description || "").toLowerCase().includes(codeQuery.toLowerCase()))
                .sort((a, b) => a.description.localeCompare(b.description)),
        [codes, codeQuery]
    );

    useEffect(() => {
        if (!codeQuery.trim()) {
            setCodesExpanded([]);
            return;
        }
        const open: string[] = [];
        if (activeCodes.length) {
            open.push("codes-active");
        }
        if (inactiveCodes.length) {
            open.push("codes-inactive");
        }
        setCodesExpanded(open);
    }, [codeQuery]);

    const renderCodeTiles = (list: Code[]) => (
        <IonGrid>
            <IonRow>
                {list.map((code) => (
                    <IonCol size="12" sizeSm="6" sizeMd="4" sizeLg="3" key={code.id}>
                        <IonItem lines="full">
                            <IonLabel className="ion-text-wrap">
                                <IonText>{code.description}</IonText>
                            </IonLabel>
                            <IonToggle
                                slot="end"
                                checked={code.status === "ACTIVE"}
                                onIonChange={(ev) => toggleCode(code, ev)}
                            />
                        </IonItem>
                    </IonCol>
                ))}
                {list.length === 0 && (
                    <IonCol size="12">
                        <IonItem lines="none">
                            <IonLabel>No items.</IonLabel>
                        </IonItem>
                    </IonCol>
                )}
            </IonRow>
        </IonGrid>
    );

    return (
        <IonAccordion value="sec-codes">
            <IonItem slot="header" color="light" lines="full">
                <IonLabel>Failed Reason Codes</IonLabel>
            </IonItem>
            <div slot="content">
                <section className="panel panel-default">
                    <IonGrid>
                        <IonRow className="ion-align-items-end">
                            <IonCol size="12" sizeMd="4">
                                <IonSearchbar
                                    value={codeQuery}
                                    placeholder="Search codes"
                                    onIonInput={(e) =>
                                        setCodeQuery((e.detail.value ?? "").toString())
                                    }
                                    onIonClear={() => setCodeQuery("")}
                                />
                            </IonCol>
                            <IonCol size="12" sizeMd="2" className="btn-col ion-align-self-end">
                                <IonButton onClick={openAdd}>
                                    <IonIcon slot="icon-only" md={addOutline} ios={addSharp} />
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                    <IonAccordionGroup
                        multiple
                        style={{ display: "grid", gap: 6 }}
                        value={codesExpanded}
                        onIonChange={(e) =>
                            setCodesExpanded(Array.isArray(e.detail.value) ? e.detail.value : [])
                        }
                    >
                        <IonAccordion value="codes-active">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Active Codes</IonLabel>
                                <IonBadge slot="end">{activeCodes.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderCodeTiles(activeCodes)}</div>
                        </IonAccordion>

                        <IonAccordion value="codes-inactive">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Inactive Codes</IonLabel>
                                <IonBadge slot="end">{inactiveCodes.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderCodeTiles(inactiveCodes)}</div>
                        </IonAccordion>
                    </IonAccordionGroup>
                </section>
            </div>
        </IonAccordion>
    );
};

export default AcaSettingsFailedReasonSection;